import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      if (attempts >= maxAttempts) { setStatus("timeout"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-[700px] mx-auto px-5 py-24 text-center min-h-[60vh] flex flex-col items-center justify-center" data-testid="checkout-status">
      {status === "checking" && (
        <>
          <Loader2 size={48} className="animate-spin text-foreground" />
          <h1 className="font-heading font-bold text-3xl uppercase tracking-tight mt-6">Confirming payment…</h1>
          <p className="text-muted-foreground mt-2 font-mono text-sm">Please wait, do not close this window.</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 size={56} className="text-accent" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-6">Order Confirmed</h1>
          <p className="text-muted-foreground mt-3">Thank you for your purchase. A confirmation has been recorded.</p>
          <Link to="/shop" className="inline-block mt-8 bg-foreground text-background px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-black/80 transition-colors">Continue Shopping</Link>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle size={56} className="text-destructive" />
          <h1 className="font-heading font-bold text-3xl uppercase tracking-tight mt-6">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-muted-foreground mt-3">{status === "timeout" ? "We couldn't confirm your payment yet. Check back shortly." : "Your payment was cancelled or failed."}</p>
          <Link to="/cart" className="inline-block mt-8 border border-foreground px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-secondary transition-colors">Back to Cart</Link>
        </>
      )}
    </div>
  );
}
